import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Card,
  CardContent,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Drawer,
  List,
  ListItem,
  ListItemText,
  StepConnector,
  stepConnectorClasses,
} from '@mui/material';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
  harmonateDashboardPageSx,
  harmonateCardSx,
  harmonateProceedButtonSx,
  harmonatePlainIconButtonSx,
  harmonateSectionHeaderSx,
  harmonateDrawerHeaderSx,
} from '../../theme/harmonateStyles';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Gavel as GavelIcon,
  Warning as WarningIcon,
  Notifications as NotificationsIcon,
  Error as ErrorOutlineIcon,
} from '@mui/icons-material';

const CollectionTierInfo = ({ vendorData, onBack }) => {
  const [selectedTier, setSelectedTier] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  const vendor = vendorData || {
    id: 3,
    accountNumber: '',
    amountDue: '',
    dueDate: '2/11/25',
    currentDate: '',
    daysOverdue: '',
    collectionTier: '',
    currentProbability: '',
  };

  const vendorFields = [
    { label: 'Vendor', value: vendor.id },
    { label: 'Account Number', value: vendor.accountNumber || '-' },
    { label: 'Amount Due', value: vendor.amountDue || '-' },
    { label: 'Due Date', value: vendor.dueDate },
    { label: 'Current Date', value: vendor.currentDate || '-' },
    { label: 'Days Overdue', value: vendor.daysOverdue || '-' },
    { label: 'Collection Tier', value: vendor.collectionTier || '-' },
    { label: 'Current Probability', value: vendor.currentProbability || '-' },
  ];

  const collectionTiers = [
    {
      tier: 1,
      title: '0-30 Days Past Due:',
      subtitle: 'Automated Reminder (email, SMS text)',
      icon: <EmailIcon sx={{ fontSize: 48, color: ht.text }} />,
      selected: selectedTier === 1,
    },
    {
      tier: 2,
      title: '30-60 Days Past Due:',
      subtitle: 'Phone Call & Customized Payment Plan',
      icon: <PhoneIcon sx={{ fontSize: 48, color: ht.text }} />,
      selected: selectedTier === 2,
    },
    {
      tier: 3,
      title: '60-90 Days Past Due:',
      subtitle: 'Final Notice & Service Suspension',
      icon: <ErrorOutlineIcon sx={{ fontSize: 48, color: ht.text }} />,
      selected: selectedTier === 3,
    },
    {
      tier: 4,
      title: '90+ Days Past Due:',
      subtitle: 'Third-Party Collections, Legal Action, Credit Bureau',
      icon: <GavelIcon sx={{ fontSize: 48, color: ht.text }} />,
      selected: selectedTier === 4,
    },
  ];

  const alerts = [
    { id: 1, message: 'Low Probability of Collection' },
    { id: 2, message: 'High Probability of Collection' },
  ];

  const handleTierSelect = (tier) => {
    setSelectedTier(tier);
  };

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const handleInitiateCollection = () => {
    navigate('/initiation');
  };

  return (
    <Box sx={harmonateDashboardPageSx}>
      {/* Section 1: Header with Title, Stepper, and Alert Icon */}
      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          p: 2,
          bgcolor: ht.white,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={onBack} sx={{ ...harmonatePlainIconButtonSx, color: ht.text, mr: 1, '&:hover': { bgcolor: ht.secondaryTab, color: ht.text } }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700, color: ht.text }}>
            Vendor {vendor.id}
          </Typography>
        </Box>
        <Box>
          <Stepper
            activeStep={0}
            sx={{ width: 200 }}
            connector={
              <StepConnector
                sx={{
                  [`& .${stepConnectorClasses.line}`]: {
                    borderColor: ht.blue,
                    borderTopWidth: 2,
                    borderRadius: 1,
                  },
                }}
              />
            }
          >
            <Step completed>
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      border: `2px solid ${ht.blue}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: ht.text,
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    L
                  </Box>
                )}
              >
                <Typography variant="caption" sx={{ fontSize: 11, color: ht.text }}>
                  Collection Tier
                </Typography>
              </StepLabel>
            </Step>
            <Step>
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      border: `2px solid ${ht.blue}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: ht.text,
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    L
                  </Box>
                )}
              >
                <Typography variant="caption" sx={{ fontSize: 11, color: ht.text }}>
                  Initiation
                </Typography>
              </StepLabel>
            </Step>
          </Stepper>
        </Box>
        <IconButton onClick={toggleDrawer(true)} sx={{ color: '#E6A817', '&:hover': { bgcolor: ht.secondaryTab } }}>
          <NotificationsIcon />
        </IconButton>
      </Paper>

      {/* Drawer for Alerts */}
      <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box sx={{ width: 300 }}>
          <Box sx={harmonateDrawerHeaderSx}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Alerts
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
          <List>
            {alerts.map((alert) => (
              <ListItem key={alert.id}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <WarningIcon sx={{ color: '#E6A817', mr: 1 }} />
                      <Typography sx={{ color: ht.text, fontWeight: 500 }}>
                        {alert.message}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
          </Box>
        </Box>
      </Drawer>

      {/* Section 2: Grid with Table and Collection Tiers */}
      <Paper elevation={0} sx={{ ...harmonateCardSx, mb: 3, p: 2, bgcolor: ht.white }}>
        <Grid container spacing={3}>
          {/* Left Column - Vendor Details */}
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ ...harmonateCardSx, p: 0, bgcolor: ht.white }}>
              <Box
                sx={{
                  ...harmonateSectionHeaderSx,
                  bgcolor: ht.listBg,
                }}
              >
                <Box sx={{ width: '40%' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ht.text }}>
                    Field Name
                  </Typography>
                </Box>
                <Box sx={{ width: '60%', borderLeft: `1px solid ${ht.line}`, pl: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ht.text }}>
                    Value
                  </Typography>
                </Box>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {vendorFields.map((field, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:nth-of-type(odd)': { bgcolor: ht.white },
                          '&:nth-of-type(even)': { bgcolor: ht.listBg },
                          '&:hover': { bgcolor: ht.secondaryTab },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 400,
                            borderBottom: `1px solid ${ht.line}`,
                            borderRight: `1px solid ${ht.line}`,
                            py: 1.5,
                            pl: 2,
                            color: ht.text,
                            width: '40%',
                          }}
                        >
                          {field.label}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: `1px solid ${ht.line}`,
                            py: 1.5,
                            pl: 2,
                            display: 'flex',
                            alignItems: 'center',
                            color: ht.text,
                            fontWeight: 500,
                          }}
                        >
                          {field.label === 'Current Probability' && vendor.currentProbability && (
                            <WarningIcon sx={{ color: '#E6A817', fontSize: 20, mr: 1 }} />
                          )}
                          {field.value}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Right Column - Collection Tiers (Sections 3-6) */}
          <Grid item xs={12} md={7}>
            <Grid container spacing={2}>
              {collectionTiers.map((tier) => (
                <Grid item xs={6} key={tier.tier}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      bgcolor: tier.selected ? ht.secondaryTab : ht.white,
                      border: tier.selected ? `2px solid ${ht.green}` : `1px solid ${ht.line}`,
                      borderRadius: ht.cardRadius,
                      minHeight: 150,
                      display: 'flex',
                      flexDirection: 'column',
                      boxShadow: ht.cardShadow,
                      '&:hover': {
                        boxShadow: ht.cardShadow,
                        transform: 'translateY(-2px)',
                        bgcolor: tier.selected ? ht.secondaryTab : ht.listBg,
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                    onClick={() => handleTierSelect(tier.tier)}
                  >
                    <CardContent
                      sx={{
                        textAlign: 'center',
                        p: 2,
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, color: ht.text, fontSize: 14 }}
                          >
                            Tier {tier.tier}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 1 }}>{tier.icon}</Box>
                      </Box>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, mb: 0.5, color: ht.text, fontSize: 13 }}
                        >
                          {tier.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: ht.text, fontSize: 11, lineHeight: 1.2 }}
                        >
                          {tier.subtitle}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Section 7: Initiate Collection Button */}
      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          display: 'flex',
          justifyContent: 'flex-end',
          bgcolor: ht.white,
          p: 2,
        }}
      >
        <Button
          variant="contained"
          size="large"
          sx={harmonateProceedButtonSx}
          disabled={!selectedTier}
          onClick={handleInitiateCollection}
        >
          Initiate Collection
        </Button>
      </Paper>
    </Box>
  );
};

export default CollectionTierInfo;